import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  useAiConcierge,
  type ConciergeCard,
  type ConciergePrefs,
  type ConciergeSurface,
} from "@/hooks/useAiConcierge";
import { conciergeTailKey, useConciergeTail } from "@/hooks/useConciergeTail";
import { useContactRequirements } from "@/hooks/useQualityConfig";
import { useMaintenanceDisabled } from "@/hooks/useMaintenanceDisabled";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/hooks/useAuth";
import { getMissingContactFields, type ContactFieldKey } from "@/lib/profileCompleteness";
import { setIntent } from "@/lib/onboardingIntent";
import { saveWelcomePrefs } from "@/lib/welcomePrefs";
import { transferWelcomePrefsToProfile } from "@/lib/captureToProfile";
import { markConciergeSeen } from "@/lib/conciergeSeen";
import {
  conciergePrefsToWelcome,
  greetingItem,
  hasSavablePrefs,
  lastTranscriptLine,
  mergeTranscript,
  noteItem,
  presentResponse,
  serverCount,
  shownListingsOf,
  SYSTEM_COPY,
  type TranscriptItem,
} from "@/lib/conciergeTranscript";

// Everything a concierge conversation knows, independent of the chrome around
// it. The home widget and the inbox thread both call this hook and hand the
// result to <ConciergeConversation />. It lives in a hook (not the view) so the
// home widget can keep the chat alive while its full-screen Dialog is closed,
// Radix unmounts the dialog's content on minimise.
//
// The transcript is reconciled against the server's on every poll (see
// mergeTranscript): history is restored on mount, the team's replies arrive
// during a takeover, and a turn sent from another tab shows up too.

export type AuthIntent = "request" | "save" | "team";

export interface ProfileFixState {
  open: boolean;
  missing: ContactFieldKey[];
  initial: { displayName: string; phone: string; introduction: string };
  setOpen: (open: boolean) => void;
  onCompleted: () => void;
}

export interface ConciergeConversationState {
  surface: ConciergeSurface;
  enabled: boolean;
  /** The concierge's name for this market. */
  name: string;
  configLoading: boolean;
  sessionId: string | null;
  maintenanceDisabled: boolean;
  items: TranscriptItem[];
  sending: boolean;
  /** History is being fetched for an existing session and nothing has arrived yet. */
  restoring: boolean;
  handoffActive: boolean;
  /** The server's soft cap was hit: only "New chat" continues. */
  sessionFull: boolean;
  /** Every listing surfaced so far, deduped, feeds the desktop panel / matches sheet. */
  shownListings: ConciergeCard[];
  draft: string;
  setDraft: (value: string) => void;
  /** Send `text`, or the current draft when omitted. Enforces the maintenance and cap locks. */
  submit: (text?: string) => Promise<void>;
  /** Resend a failed line (removes the failed item first). */
  retry: (item: TranscriptItem) => void;
  /** Send a request the concierge proposed (the renter's tap). */
  sendProposed: (item: TranscriptItem) => void;
  dismissProposed: (item: TranscriptItem) => void;
  confirming: boolean;
  /** Start over: new session, greeting only. */
  reset: () => void;
  latestPrefs: ConciergePrefs | null;
  canSavePrefs: boolean;
  savePrefs: () => void;
  detailSlug: string | null;
  openDetail: (slug: string) => void;
  closeDetail: () => void;
  askAbout: (slug: string, title: string) => void;
  authIntent: AuthIntent | null;
  openAuth: (intent: AuthIntent) => void;
  closeAuth: () => void;
  /** "Talk to the team": signed-in → the inbox team pane, anonymous → sign-in first. */
  escalate: () => void;
  profileFix: ProfileFixState;
}

interface Options {
  surface: ConciergeSurface;
  /** Runs as a message leaves (the home widget expands to full screen here). */
  onBeforeSend?: () => void;
  /**
   * Whether the conversation is on screen. The transcript is polled every
   * 6 s only while it is; the home page's collapsed pill passes false, so a
   * returning visitor who never opens the chat costs nothing.
   */
  active?: boolean;
}

const RESUME_REQUEST = "Done, my profile is complete now. Please send the request.";
const MAINTENANCE_COPY = "We are temporarily paused for maintenance. Please try again soon.";
export const TEAM_INBOX_PATH = "/messages?c=team";

export function useConciergeConversation({ surface, onBeforeSend, active = true }: Options): ConciergeConversationState {
  const { enabled, greeting, name, configLoading, sessionId, send, confirmContact, fetchTail, resetSession } = useAiConcierge({ surface });
  const tenant = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const maintenanceDisabled = useMaintenanceDisabled();
  const { data: contactReqs } = useContactRequirements(tenant.id);
  const tail = useConciergeTail({ sessionId: active ? sessionId : null, fetchTail });

  const [items, setItems] = useState<TranscriptItem[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [handoffActive, setHandoffActive] = useState(false);
  const [sessionFull, setSessionFull] = useState(false);
  const [latestPrefs, setLatestPrefs] = useState<ConciergePrefs | null>(null);
  const [detailSlug, setDetailSlug] = useState<string | null>(null);
  const [authIntent, setAuthIntent] = useState<AuthIntent | null>(null);
  // Quick-complete gate (mirrors PropertyDetail): opened when the concierge
  // returns need_profile; collects only the tenant-required missing fields.
  const [profileFixOpen, setProfileFixOpen] = useState(false);
  const [profileFixMissing, setProfileFixMissing] = useState<ContactFieldKey[]>([]);
  const [profileFixInitial, setProfileFixInitial] = useState({ displayName: "", phone: "", introduction: "" });
  const notedHandoff = useRef(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // The greeting is always the first line, on both surfaces.
  useEffect(() => {
    if (enabled && greeting) {
      setItems((m) => (m.some((x) => x.kind === "greeting") ? m : [greetingItem(greeting), ...m]));
    }
  }, [enabled, greeting]);

  // Reconcile with the server transcript on every poll (restores history on
  // mount, brings the team's lines during a takeover). Skipped while a send is
  // in flight so the optimistic lines are never displaced mid-turn.
  const tailData = tail.data;
  useEffect(() => {
    if (!tailData || sending) return;
    setHandoffActive(tailData.handoff);
    setItems((cur) => mergeTranscript(cur, tailData.display));
    // Whatever is on screen now has been seen (the inbox row's unread dot).
    const last = lastTranscriptLine(tailData.display);
    if (last?.at) markConciergeSeen(tenant.id, last.at);
  }, [tailData, sending, tenant.id]);

  // Announce the takeover once when it begins (and re-arm when it ends).
  useEffect(() => {
    if (handoffActive && !notedHandoff.current) {
      notedHandoff.current = true;
      setItems((m) => [...m, noteItem("handoff", SYSTEM_COPY.handoff, serverCount(m))]);
    }
    if (!handoffActive) notedHandoff.current = false;
  }, [handoffActive]);

  const shownListings = useMemo(() => shownListingsOf(items), [items]);
  const restoring = !!sessionId && tail.isLoading && serverCount(items) === 0;

  const submit = async (raw?: string) => {
    if (maintenanceDisabled) {
      toast.info(MAINTENANCE_COPY);
      return;
    }
    if (sessionFull) {
      toast.info("This chat is full. Start a new one to continue.");
      return;
    }
    const text = (raw ?? draft).trim();
    if (!text || sending) return;
    const sent: TranscriptItem = {
      id: `s${serverCount(itemsRef.current)}`,
      role: "user",
      text,
      at: new Date().toISOString(),
    };
    setItems((m) => [...m, sent]);
    setDraft("");
    setSending(true);
    onBeforeSend?.();
    const res = await send(text);
    const cur = itemsRef.current.some((m) => m.id === sent.id) ? itemsRef.current : [...itemsRef.current, sent];
    const presented = presentResponse(res, cur, sent, new Date().toISOString());
    setItems(presented.items);
    setSending(false);
    if (presented.prefs) setLatestPrefs(presented.prefs);
    if (presented.handoff) setHandoffActive(true);
    if (presented.sessionFull) setSessionFull(true);
    if (presented.gate === "need_auth") setAuthIntent("request");
    else if (presented.gate === "need_profile") void openProfileFix();
    // Let the server's timestamps replace the optimistic ones.
    if (!res.error) {
      void queryClient.invalidateQueries({ queryKey: conciergeTailKey(tenant.id, res.sessionId ?? sessionId) });
    }
  };

  // The renter's tap on a proposed request. Nothing is sent before this.
  const [confirming, setConfirming] = useState(false);
  const sendProposed = async (item: TranscriptItem) => {
    if (!item.pending || confirming) return;
    setConfirming(true);
    const res = await confirmContact(item.pending);
    setConfirming(false);
    const at = new Date().toISOString();
    setItems((m) => {
      const rest = m.filter((x) => x.id !== item.id);
      const n = serverCount(rest);
      const reply: TranscriptItem = {
        id: `s${n}`,
        role: "assistant",
        text: res.reply ?? (res.error ? "I couldn't send that request. Please try again." : ""),
        at,
      };
      const next = [...rest, reply];
      if (res.action === "request_sent") next.push(noteItem("request_sent", SYSTEM_COPY.request_sent, n + 1));
      return next;
    });
    if (res.action === "need_auth") setAuthIntent("request");
    else if (res.action === "need_profile") void openProfileFix();
    if (!res.error) void queryClient.invalidateQueries({ queryKey: conciergeTailKey(tenant.id, res.sessionId ?? sessionId) });
  };
  const dismissProposed = (item: TranscriptItem) => setItems((m) => m.filter((x) => x.id !== item.id));

  const retry = (item: TranscriptItem) => {
    if (item.kind !== "failed") return;
    setItems((m) => m.filter((x) => x.id !== item.id));
    void submit(item.text);
  };

  const reset = () => {
    if (sending) return;
    resetSession();
    setItems(greeting ? [greetingItem(greeting)] : []);
    setDraft("");
    setHandoffActive(false);
    notedHandoff.current = false;
    setSessionFull(false);
    setLatestPrefs(null);
    setDetailSlug(null);
  };

  // Fetch current values, compute what's actually missing per the tenant's
  // requirements, and open the quick-complete dialog. If a race means nothing
  // is missing anymore, resume the conversation straight away.
  const openProfileFix = async () => {
    if (!user) {
      setAuthIntent("request");
      return;
    }
    const [{ data: prof }, { data: ph }] = await Promise.all([
      supabase.from("profiles").select("display_name, introduction").eq("user_id", user.id).maybeSingle(),
      supabase.rpc("get_own_phone"),
    ]);
    const values = {
      displayName: prof?.display_name ?? "",
      phone: (ph as string | null) ?? "",
      introduction: prof?.introduction ?? "",
    };
    const missing = getMissingContactFields(values, contactReqs);
    if (missing.length === 0) {
      void submit(RESUME_REQUEST);
      return;
    }
    setProfileFixInitial(values);
    setProfileFixMissing(missing);
    setProfileFixOpen(true);
  };

  const savePrefs = () => {
    if (!latestPrefs) return;
    saveWelcomePrefs(conciergePrefsToWelcome(latestPrefs, tenant));
    if (user) {
      // Already signed in, write straight to the profile; alerts flow from there.
      transferWelcomePrefsToProfile(user.id, { id: tenant.id, areas: tenant.areas, areaSlugs: tenant.areaSlugs }).then(() =>
        toast.success("Preferences saved. We'll email you when a match is listed."),
      );
    } else {
      setIntent("renter");
      setAuthIntent("save"); // prefilled registration → /welcome wizard
    }
  };

  const escalate = () => {
    if (user) navigate(TEAM_INBOX_PATH);
    else setAuthIntent("team");
  };

  return {
    surface,
    enabled,
    name,
    configLoading,
    sessionId,
    maintenanceDisabled,
    items,
    sending,
    restoring,
    handoffActive,
    sessionFull,
    shownListings,
    draft,
    setDraft,
    submit,
    retry,
    sendProposed,
    dismissProposed,
    confirming,
    reset,
    latestPrefs,
    canSavePrefs: hasSavablePrefs(latestPrefs),
    savePrefs,
    detailSlug,
    openDetail: (slug) => setDetailSlug(slug),
    closeDetail: () => setDetailSlug(null),
    askAbout: (_slug, title) => {
      setDetailSlug(null);
      void submit(`Can you tell me more about ${title}?`);
    },
    authIntent,
    openAuth: (intent) => setAuthIntent(intent),
    closeAuth: () => setAuthIntent(null),
    escalate,
    profileFix: {
      open: profileFixOpen,
      missing: profileFixMissing,
      initial: profileFixInitial,
      setOpen: setProfileFixOpen,
      onCompleted: () => void submit(RESUME_REQUEST),
    },
  };
}
